import styled, { css } from 'styled-components';
import { NavLink } from 'react-router-dom';
import { Box, BoxProps, Avatar, AvatarStyled, ModalBackdropStyled, Icon } from '@bechtel/gds';

export interface NavProps {
  isCollapsed: boolean;
}

type BoxNavProps = BoxProps & NavProps;

export const navWidth = '264px';
export const collapsedWidth = '70px';

export const NavBackdropStyled = styled(ModalBackdropStyled)`
  /* set higher z-index so it floats on other elements*/
  z-index: 1300;
`;

export const NavStyled = styled.div<NavProps>`
  background-image: ${({ theme }) => `
    linear-gradient(194deg, ${theme.colors.bechtel.blue[800]} 0%, ${theme.colors.bechtel.blue[1000]} 100%)
  `};
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
  left: 0;
  top: 0;
  width: ${({ isCollapsed }) => (isCollapsed ? collapsedWidth : navWidth)};
  flex-shrink: 0;
  transition: width 40ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
`;

export const LogoStyled = styled.div<NavProps>`
  display: block;
  margin-bottom: ${({ theme }) => theme.spacings[700]};
  margin-left: auto;
  margin-right: auto;
  padding-top: ${({ theme }) => theme.spacings[800]};
  width: calc(${({ isCollapsed }) => (isCollapsed ? collapsedWidth : navWidth)} / 1.75);
`;

export const LinkStyled = styled(NavLink)`
  position: relative;
  align-items: center;
  border-left: 4px solid transparent;
  display: flex;
  text-decoration: none;
  transition-duration: 240ms;
  transition-property: background-color, border-color, color;
  transition-timing-function: ease-in-out;
  user-select: none;
  white-space: nowrap;

  ${({ theme }) => css`
    color: ${theme.colors.white};
    border-bottom-left-radius: ${theme.radii[200]};
    border-top-left-radius: ${theme.radii[200]};
    line-height: ${theme.fontSizes[700]}; /* higher explicit line-height required for variable icon heights */
    padding-top: ${theme.spacings[450]};
    padding-bottom: ${theme.spacings[450]};
    padding-left: ${theme.spacings[500]};
    margin-left: ${theme.spacings[400]};
  `};

  &:active,
  &.active {
    background-color: ${({ theme }) => theme.colors.bechtel.blue[700]};
    border-color: ${({ theme }) => theme.colors.bechtel.blue[500]};
  }

  &:hover {
    background-color: ${({ theme }) => theme.colors.bechtel.blue[700]};
    transition-duration: 80ms;
  }
`;

export const AvatarLinkStyled = styled(LinkStyled)`
  ${({ theme }) => css`
    font-family: ${({ theme }) => theme.fontFamilies.graphikCompact};
    font-weight: ${theme.fontWeights[600]};
    font-size: ${theme.fontSizes[300]};
  `};
  & > * {
    margin-right: ${({ theme }) => theme.spacings[400]};
  }
`;

export const LinkNoTextStyled = styled(LinkStyled)`
  margin-left: 0;
  ${AvatarStyled} {
    border-width: 1px;
  }
`;

export const ButtonStyled = styled.button`
  position: relative;
  align-items: center;
  border: none;
  border-left: 4px solid transparent;
  display: flex;
  text-decoration: none;
  transition-duration: 240ms;
  transition-property: background-color, border-color, color;
  transition-timing-function: ease-in-out;
  user-select: none;
  white-space: nowrap;
  cursor: pointer;
  background: transparent;
  width: 100%;

  ${({ theme }) => css`
    color: ${theme.colors.white};
    border-bottom-left-radius: ${theme.radii[200]};
    border-top-left-radius: ${theme.radii[200]};
    line-height: ${theme.fontSizes[700]};
    padding-top: ${theme.spacings[450]};
    padding-bottom: ${theme.spacings[450]};
    padding-left: ${theme.spacings[500]};
    margin-left: ${theme.spacings[400]};
  `};

  &:hover {
    background-color: ${({ theme }) => theme.colors.bechtel.blue[700]};
    transition-duration: 80ms;
  }
`;

export const ButtonNoTextStyled = styled(ButtonStyled)`
  margin-left: 0;
  justify-content: center;
  padding-left: 0;
`;

export const NavBottomPlacementStyled = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.bechtel.blue[700]};
  bottom: 0;
  padding-top: ${({ theme }) => theme.spacings[400]};
  width: 100%;
`;

export const CollapseIconStyled = styled(Box)<BoxNavProps>`
  width: 100%;
  ${({ theme }) => css`
    min-height: ${theme.spacings[800]};
    border-radius: ${theme.spacings[500]};
    align-items: center;
    justify-content: center;
    cursor: pointer;
  `}
  ${({ isCollapsed }) =>
    !isCollapsed &&
    css`
      padding-right: ${({ theme }) => theme.spacings[500]};
      justify-content: flex-end;
    `}
`;

export const UserSettingsIconStyled = styled(Box).attrs({
  alignItems: { sm: 'center' },
  justifyContent: { sm: 'center' },
})`
  ${({ theme }) => css`
    min-height: ${theme.spacings[800]};
    border-radius: ${theme.spacings[500]};
    cursor: pointer;
  `}
`;

export const IconStyled = styled(Icon).attrs({
  iconStyle: 'duo',
  icon: 'user-cog',
  fillColor: 'white',
})`
  width: 28px;
  & > svg {
    height: 28px;
  }
`;

export const AvatarRegularStyled = styled(Avatar)`
  border-width: 1px;
  height: 40px;
  width: 40px;
  color: ${({ theme }) => theme.colors.grey['1000']};
`;

interface SwitchProjectProps extends NavProps {
  canSwitchProject: boolean;
}
export const EditProjectStyled = styled(Box).attrs({
  py: { sm: '200' },
  alignItems: { sm: 'center' },
  mt: { sm: '400' },
})<SwitchProjectProps>`
  ${({ theme }) => css`
    border-top: 1px solid ${theme.colors.bechtel.blue['700']};
    border-bottom: 1px solid ${theme.colors.bechtel.blue['700']};
  `}
  ${({ isCollapsed }) =>
    !isCollapsed
      ? css`
          justify-content: space-between;
        `
      : css`
          justify-content: space-around;
        `}
   ${({ canSwitchProject }) =>
    canSwitchProject &&
    css`
      cursor: pointer;
    `}
    ${({ isCollapsed, canSwitchProject }) =>
    isCollapsed &&
    !canSwitchProject &&
    css`
      visibility: hidden;
    `}
`;

export const NotificationStyled = styled(Box).attrs({ alignItems: { sm: 'center' }, justifyContent: { sm: 'center' } })`
  ${({ theme }) => css`
    position: absolute;
    right: ${theme.spacings['500']};
    border-radius: ${theme.radii['400']};
    background-color: ${theme.colors.bechtel.red};
    border: 1px solid ${theme.colors.bechtel.blue['800']};
    height: ${theme.spacings['500']};
    width: 21px;
    color: ${theme.colors.white};
    font-size: ${theme.fontSizes['300']};
  `}
`;

export const NavContainerStyled = styled(Box).attrs({
  flexDirection: { sm: 'column' },
  flexGrow: { sm: '1' },
})`
  overflow-x: hidden;
  overflow-y: auto;
`;
